/**
 * Les libellés et les tons de l'écran « Demandes » — le vocabulaire du prototype validé,
 * M3 pour les valeurs.
 *
 * Hors du module d'écran parce que **deux modules en ont besoin** : la ligne du tableau les
 * affiche, et le schéma d'URL en dérive les valeurs de ses filtres. Sans table partagée, le
 * libellé serait écrit deux fois et l'option du filtre se désynchroniserait de la pastille au
 * premier renommage.
 */
import {
  appointmentSlot,
  INQUIRY_STATUS_LABELS,
  inquiryStatusLabel,
  inspectionProgressLabel,
} from '@ghp/domain'
import type { InspectionProgress } from '@ghp/domain'
import type { BadgeTone, TagTone } from '@ghp/ui'
import type { InquiryClientKind, InquiryDisplayStatus, InquiryKind } from '../fixtures/inquiry-list'

/**
 * Libellés et tons des badges d'état — la colonne « État ».
 *
 * Les sept valeurs de `Inquiry.status` (M3), dans l'ordre du cycle de vie. Les **libellés**
 * viennent de `INQUIRY_STATUS_LABELS` (`@ghp/domain`) : l'export du tableau les imprime, et
 * recopier les sept mots ici ferait diverger le fichier de la colonne au premier renommage.
 *
 * ⚠️ Le prototype affichait aussi « Sans RDV » et « RDV fait ». Ce sont des prédicats sur la
 * présence d'un rendez-vous, pas des statuts (M3) : « RDV pris » en est un, dérivé par `stateOf`.
 *
 * ⚠️ Trois statuts partagent le ton `info`, et ce n'est pas un oubli : `Badge` n'a que quatre
 * tons, les trois autres étant pris par ce qui demande une action, ce qui est acquis et ce qui ne
 * se fera pas. C'est le libellé qui les distingue.
 */
export const STATUS_PRESENTATION: Record<InquiryDisplayStatus, { label: string; tone: BadgeTone }> =
  {
    received: { label: INQUIRY_STATUS_LABELS.received, tone: 'warn' },
    assigned: { label: INQUIRY_STATUS_LABELS.assigned, tone: 'info' },
    in_progress: { label: INQUIRY_STATUS_LABELS.in_progress, tone: 'info' },
    submitted: { label: INQUIRY_STATUS_LABELS.submitted, tone: 'info' },
    validated: { label: INQUIRY_STATUS_LABELS.validated, tone: 'ok' },
    archived: { label: INQUIRY_STATUS_LABELS.archived, tone: 'ok' },
    dropped: { label: INQUIRY_STATUS_LABELS.dropped, tone: 'bad' },
  }

/**
 * Les états que **rien ne recouvre** : une demande validée, archivée ou abandonnée a fini sa
 * course, et son état ne se laisse pas remplacer par un avis de passage.
 *
 * ⚠️ **Le cas existe vraiment**, il n'est pas théorique : une demande passée deux fois dont la
 * première visite n'a pas pu être honorée garde son avis ouvert, et la seconde peut très bien
 * avoir été validée. Sans cette liste, la ligne annoncerait « Avis de passage » sur un dossier
 * clos — exactement le contraire de ce que le gestionnaire doit lire.
 */
const SETTLED_STATUSES = [
  'validated',
  'archived',
  'dropped',
] as const satisfies readonly InquiryDisplayStatus[]

/**
 * Ce que la colonne « État » affiche, avis de passage compris.
 *
 * ⚠️ **« Avis de passage » n'est pas une valeur d'`Inquiry.status`, et ça ne doit pas le
 * devenir** : le cycle de vie de M3 ne le connaît pas, et l'y ajouter toucherait la machine à
 * états de tout le produit pour un affichage. C'est un **fait daté à côté** du statut, comme la
 * livraison client (M26) — la demande garde le sien, la colonne compose ce qu'elle montre.
 *
 * ⚠️ **L'avis remplace la pastille, il ne s'y ajoute pas.** Le prototype tient un seul état par
 * ligne (`etatLabel`, cinq valeurs mutuellement exclusives), et deux pastilles côte à côte
 * laisseraient le gestionnaire chercher laquelle fait foi. Ce qui est perdu ne l'est pas
 * vraiment : un avis n'existe que sur une demande affectée ou en cours, donc le statut masqué
 * est déductible — alors que l'avis, lui, ne se lit nulle part ailleurs sur cette ligne.
 *
 * ⚠️ **Le ton `warn`, partagé avec « Reçue »**, et c'est assumé pour la raison que
 * `STATUS_PRESENTATION` donne déjà : `Badge` n'a que quatre tons, et celui-ci dit « il y a
 * quelque chose à faire ». C'est le libellé qui distingue.
 */
export function stateOf(
  status: InquiryDisplayStatus,
  passageNoticedAt: string | null,
  {
    progress = null,
    hasAppointment = false,
    messageLeftAt = null,
  }: {
    /** La dernière étape du contrôle (M42). */
    progress?: InspectionProgress | null
    /** Un rendez-vous en vigueur : une demande affectée se lit « RDV pris ». */
    hasAppointment?: boolean
    /** Le dernier « Message laissé » (M46) : une demande reçue se lit « En attente ». */
    messageLeftAt?: string | null
  } = {},
): { label: string; tone: BadgeTone; detail: string | null } {
  const settled = (SETTLED_STATUSES as readonly InquiryDisplayStatus[]).includes(status)

  if (passageNoticedAt !== null && !settled) {
    return {
      label: 'Avis de passage',
      tone: 'warn',
      // `appointmentSlot`, pas `toLocaleDateString` : le jour de Paris, celui que l'e-mail d'avis
      // cite, quel que soit le fuseau du poste (M24).
      detail: appointmentSlot(passageNoticedAt)?.day ?? null,
    }
  }

  // « En attente » (Sentry BACK-OFFICE-D) : une demande reçue sur laquelle le bureau a laissé un
  // message (M46). Même parti que l'avis : un fait daté qui remplace le libellé, pas un statut.
  if (messageLeftAt !== null && status === 'received') {
    return {
      label: 'En attente',
      tone: 'warn',
      detail: appointmentSlot(messageLeftAt)?.day ?? null,
    }
  }

  // Deux replis, pas deux `!` : un statut que cette table ignore s'affiche **brut** au ton
  // d'alerte plutôt que de démonter la rangée pendant le rendu (M21).
  const known = STATUS_PRESENTATION[status] ?? { label: status, tone: 'warn' satisfies BadgeTone }

  return {
    ...known,
    // « RDV pris » sur la même pastille qu'« Affectée » (BACK-OFFICE-Q) : seul le libellé change.
    ...(status === 'assigned' && { label: inquiryStatusLabel(status, hasAppointment) }),
    // L'étape du contrôle sous la pastille (M42) — « Commencé à 10 h 12 » —, tant que le contrôle
    // est entre la tablette et la validation : au-delà, elle ne dit plus rien d'utile.
    detail:
      progress !== null && (FIELD_STATUSES as readonly InquiryDisplayStatus[]).includes(status)
        ? inspectionProgressLabel(progress)
        : null,
  }
}

/** Les statuts où l'étape terrain se lit encore : affecté, en cours, transmis. */
const FIELD_STATUSES = [
  'assigned',
  'in_progress',
  'submitted',
] as const satisfies readonly InquiryDisplayStatus[]

/**
 * Libellés et tons des types de demande — la pastille de la colonne « Adresse ». Les six du
 * prototype, dans son ordre et ses teintes (`typeMeta` de `mocks/ghp-bo/data.js`) — sauf
 * « Rétrocession », écrit en entier depuis la recette (#439, `INQUIRY_PURPOSE_LABELS`).
 */
export const KIND_PRESENTATION: Record<InquiryKind, { label: string; tone: TagTone }> = {
  sale: { label: 'Vente', tone: 'amber' },
  systematic: { label: 'Systématique', tone: 'neutral' },
  syam: { label: 'SYAM', tone: 'blue' },
  pollution: { label: 'Pollution', tone: 'red' },
  retro: { label: 'Rétrocession', tone: 'violet' },
  pre_works: { label: 'Avant travaux', tone: 'green' },
  // Hors prototype (#505) : le ton neutre, celui d'un type que l'écran ne connaît pas.
  investigation: { label: 'Enquête', tone: 'neutral' },
}

/**
 * Libellés et tons de la nature du client — le filtre et le tableau de bord, **trois
 * familles** (le regroupement des cinq catégories de M10, cf. `InquiryClientKind`).
 *
 * Les trois libellés sont **ceux du prototype**, pris à son tableau de bord (« Suivi par type
 * de client ») : c'est le seul endroit qui nomme les trois familles ensemble. Le répertoire
 * dit « Promoteurs, syndics & bailleurs » pour la même famille que « Privé » — juste, mais
 * trop long pour une pastille de tableau **et** pour une option de filtre, qui reprend ce
 * même libellé (une seule écriture, cf. `CLIENT_KIND_OPTIONS`).
 *
 * ⚠️ Les tons viennent du **même** tableau de bord, et pas de la liste des demandes : le
 * prototype se contredit là-dessus. Sa liste colore « Privé » en violet (`.pill-priv`),
 * parce qu'elle n'a que deux pastilles à distinguer ; son tableau de bord, qui montre les
 * trois familles côte à côte, donne bleu / acier / violet. C'est la seule source qui tranche
 * à trois, donc elle gagne.
 *
 * L'acier n'est pas un ton nommé de `Tag` — c'est son ton **`neutral`**, dont la surface et
 * le texte sont précisément les `--steel-100` / `--steel-700` de la ligne « Privé » du
 * tableau de bord. Rien à ajouter au design system, rien à inventer non plus.
 */
export const CLIENT_KIND_PRESENTATION: Record<InquiryClientKind, { label: string; tone: TagTone }> =
  {
    municipality: { label: 'Commune', tone: 'blue' },
    private_professional: { label: 'Privé', tone: 'neutral' },
    individual: { label: 'Particulier', tone: 'violet' },
  }

/**
 * Les types de demande, dans l'ordre d'affichage. **Dérivés** de `KIND_PRESENTATION`, qui
 * est exhaustive par son type : ajouter un type au `Record` l'ajoute au schéma d'URL et à
 * la liste déroulante du filtre, sans seconde liste de littéraux à tenir à jour.
 */
export const INQUIRY_KIND_VALUES = Object.keys(KIND_PRESENTATION) as InquiryKind[]

/** Même dérivation pour la nature du client, depuis `CLIENT_KIND_PRESENTATION`. */
export const CLIENT_KIND_VALUES = Object.keys(CLIENT_KIND_PRESENTATION) as InquiryClientKind[]

/**
 * Ce qu'une ligne du tableau ouvre — **le branchement du prototype**, écrit une seule fois.
 *
 * Le contrôleur du prototype tranche sur l'état de la demande
 * (`mocks/ghp-bo/app.js:1220-1226`, lu le 21/09/2026) : `aValider` et `valide` vont à l'éditeur
 * de contrôle, **tous les autres états** ouvrent la boîte de gestion — l'équipe et le
 * rendez-vous. Et il le fait deux fois : au rendu, en choisissant l'attribut que la rangée porte
 * (`screens.js:1308`, `:1611`, `:1620`), et au clic, dans son gestionnaire délégué.
 *
 * ⚠️ **Le code navigait toujours vers l'éditeur**, donc vers « rien à lire » pour tout ce qui
 * n'est pas encore transmis — c'est-à-dire la majorité des lignes d'une file de travail. Il n'y
 * a pas de contrôle à lire avant qu'une tablette en dépose un (D4).
 *
 * ⚠️ **`archived` et `dropped` vont à l'éditeur, en lecture**, et non à la gestion. Le prototype
 * n'en montre jamais dans sa liste, mais l'onglet « Toutes » les montre ici : ouvrir leur gestion
 * posait un rendez-vous qu'aucune tablette ne verra, et la confirmation partait quand même à
 * l'usager. Une demande archivée porte un contrôle à relire ; une abandonnée, rien à planifier.
 *
 * ⚠️ **Le statut, pas la forme qui le porte** : le tableau des demandes le nomme `displayStatus`,
 * la fiche équipe `status`. Prendre l'objet obligerait l'une des deux à s'y conformer.
 */
export function openTarget(status: InquiryDisplayStatus): 'editor' | 'manage' {
  return status === 'submitted' ||
    status === 'validated' ||
    status === 'archived' ||
    status === 'dropped'
    ? 'editor'
    : 'manage'
}
